import { readRoomCards } from "../widget/room-cards";
import type {
  AgentPublic,
  ConversationSummary,
  ChatMessage,
} from "../widget/state";
import { getWidgetHost } from "../utils/auth";

export class ChatGrowAPI {
  constructor(private readonly baseUrl: string) {}

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    const ac = new AbortController();
    const timeout = window.setTimeout(() => ac.abort(), 15000);

    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        signal: ac.signal,
        headers: {
          "Content-Type": "application/json",
          "X-Widget-Origin": getWidgetHost(),
          ...(init?.headers || {}),
        },
        credentials: "omit",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `API ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
        );
      }

      return (await res.json()) as T;
    } finally {
      clearTimeout(timeout);
    }
  }

  /* =======================
     Public API
  ======================== */

  getAgentPublic(agentId: string): Promise<AgentPublic> {
    return this.json<AgentPublic>(
      `/public/chat/agent/${encodeURIComponent(agentId)}`,
    );
  }

  createConversation(
    agentId: string,
    visitorId: string,
    forceNew: boolean = false,
  ): Promise<{ conversationId: string }> {
    return this.json(`/public/chat/conversations`, {
      method: "POST",
      body: JSON.stringify({ agentId, visitorId, forceNew }),
    });
  }

  renameConversation(conversationId: string, title: string): Promise<void> {
    return this.json(`/public/chat/conversations/${conversationId}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    });
  }

  listConversations(
    agentId: string,
    visitorId: string,
  ): Promise<ConversationSummary[]> {
    return this.json(
      `/public/chat/conversations?agentId=${encodeURIComponent(
        agentId,
      )}&visitorId=${encodeURIComponent(visitorId)}`,
    );
  }

  async loadConversation(conversationId: string): Promise<{ messages: ChatMessage[]; status?: string }> {
    const result = await this.json<{ messages: ChatMessage[]; status?: string }>(
      `/public/chat/conversations/${encodeURIComponent(conversationId)}`);
    return { ...result, messages: (result.messages ?? []).map((message) => {
      const roomCards = readRoomCards(message.toolCalls);
      return { ...message, ...(roomCards ? { roomCards } : {}) };
    }) };
  }

  escalateConversation(conversationId: string): Promise<void> {
    return this.json(
      `/public/chat/conversations/${encodeURIComponent(conversationId)}/escalate`,
      { method: "PATCH" },
    );
  }

  updateVisitor(
    agentId: string,
    visitorId: string,
    name?: string,
    email?: string,
  ): Promise<void> {
    return this.json(`/public/chat/visitors`, {
      method: "PATCH",
      body: JSON.stringify({ agentId, visitorId, name, email }),
    });
  }

  confirmSchedulingBooking(
    conversationId: string,
    data: { start_time: string; name: string; email: string; timezone: string },
  ): Promise<{ success: boolean; message: string; conversationId: string }> {
    return this.json(
      `/public/chat/conversations/${encodeURIComponent(conversationId)}/scheduling/bookings`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }
}
